/**
 * PROYOMNI MAHSULOT UCHUN BO'LISH — sof hisob.
 *
 * Omborchi javon oldida turib shunday o'ylaydi: "shu 2 metrlik o'ringa
 * boltni 30 santimetrga qo'yaman, gaykani 40 ga, qolgani hozircha bo'sh
 * tursin". Ya'ni bo'lish GEOMETRIYA emas — u MAHSULOTdan boshlanadi:
 * avval nima qo'yilishi tanlanadi, keyin unga qancha joy kerakligi
 * aytiladi. Ilgari oyna faqat santimetr so'rardi va "bu raqamlar nima
 * uchun" degan savol javobsiz qolardi.
 *
 * Nega alohida fayl: bu hisob oynadan tashqarida ham to'g'ri bo'lishi
 * kerak va uni komponent ichida qoldirish sinovdan tashqarida qoldirardi.
 * Bir santimetrlik xato javonga sig'maydigan o'rin yasaydi va u faqat
 * omborda, temir bilan uchrashganda ma'lum bo'ladi.
 *
 * BIRLIK — SANTIMETR, butun son. Proyom o'lchami metrda saqlanadi, lekin
 * ekranda "0.3" emas "30" teriladi: kasr nuqtasi eng oson xato manbai.
 */

/** Ekran birligi metrda: 1 sm. */
const UNIT_M = 0.01;

/** Yangi qismga taklif qilinadigan kenglik, sm. */
const DEFAULT_PART_CM = 50;

export interface SplitPart {
  /** Qism kengligi, sm — butun va musbat. */
  widthCm: number;
  /**
   * Shu qism AJRATILGAN mahsulotlar. Bo'sh ro'yxat — bo'sh o'rin: u ham
   * katak bo'ladi va o'z etiketkasini oladi, faqat hech kimga
   * biriktirilmagan.
   *
   * RO'YXAT, bitta identifikator emas: bir o'lchamdagi boltlar bilan
   * gaykalar bitta o'ringa yonma-yon qo'yiladi va ularni ikki o'ringa
   * ajratish javonda joy isrof qilardi.
   */
  nomenclatureIds: string[];
}

export interface SplitSummary {
  /** Qismlar yig'indisi, sm. */
  sumCm: number;
  /**
   * Taqsimlanmagan joy, sm. Manfiy — qismlar proyomdan oshib ketgan.
   *
   * Musbat qoldiq XATO EMAS: u alohida bo'sh o'rin bo'ladi. Omborchi
   * "menga shu mahsulot uchun 30 sm kerak" deydi, qolgan 170 santimetrni
   * kimgadir taqsimlashga majbur emas.
   */
  freeCm: number;
  /** Saqlashga tayyor: yig'indi proyomga sig'adi va har qism musbat. */
  valid: boolean;
}

/**
 * Nusxa — mahsulot ro'yxati ham YANGI massiv bo'lib.
 *
 * Yuzaki nusxada ro'yxat ikki qism orasida umumiy qolardi va bir
 * qismga mahsulot qo'shish uni ikkinchisida ham paydo qilardi.
 */
const clonePart = (part: SplitPart): SplitPart => ({
  widthCm: part.widthCm,
  nomenclatureIds: [...part.nomenclatureIds],
});

export const splitSummary = (parts: readonly SplitPart[], bayCm: number): SplitSummary => {
  const sumCm = parts.reduce((total, part) => total + part.widthCm, 0);
  return {
    sumCm,
    freeCm: bayCm - sumCm,
    valid:
      parts.length > 0 &&
      parts.every((part) => Number.isInteger(part.widthCm) && part.widthCm > 0) &&
      sumCm <= bayCm,
  };
};

/**
 * Saqlashga ketadigan TO'LIQ ro'yxat: taqsimlanmagan joy oxirida bitta
 * bo'sh o'rin bo'lib qo'shiladi.
 *
 * Nega qo'shiladi: proyomda nomsiz tuynuk qolishi mumkin emas — u na
 * katak, na bo'shliq, unga hech narsa joylashtirib bo'lmaydi va u
 * etiketkasiz qoladi. Yig'indi esa proyomga ANIQ teng bo'lishi shart,
 * aks holda javonning uzunligi o'zgarib ketardi.
 */
export const withFreeSpace = (parts: readonly SplitPart[], bayCm: number): SplitPart[] => {
  const { freeCm } = splitSummary(parts, bayCm);
  const copied = parts.map(clonePart);
  return freeCm > 0 ? [...copied, { widthCm: freeCm, nomenclatureIds: [] }] : copied;
};

/**
 * BUTUN o'rin bitta mahsulotga — boshlang'ich holat.
 *
 * Taqsimlashning eng ko'p uchraydigan holati aynan shu: o'rin bor,
 * unga bitta mahsulot qo'yiladi va hech narsa bo'linmaydi. Bo'lish esa
 * shundan kelib chiqadi — "bu mahsulotga hammasi kerak emas, 30 sm
 * yetadi" deyilganda qolgani o'zi ikkinchi o'rin bo'lib ajraladi.
 */
export const wholeBay = (
  bayCm: number,
  nomenclatureIds: readonly string[] = [],
): SplitPart[] => [{ widthCm: bayCm, nomenclatureIds: [...nomenclatureIds] }];

/**
 * Yangi qism qo'shadi — bo'sh joydan.
 *
 * Bo'sh joy qolmagan bo'lsa hech narsa o'zgarmaydi: qism qo'shish
 * boshqasining joyini tortib olishi kerak emas, aks holda odam bitta
 * tugma bosib, oldin kiritgan o'lchamini yo'qotib qo'yardi.
 */
export const addPart = (parts: readonly SplitPart[], bayCm: number): SplitPart[] => {
  const { freeCm } = splitSummary(parts, bayCm);
  if (freeCm < 1) return parts.map(clonePart);
  return [
    ...parts.map(clonePart),
    { widthCm: Math.min(freeCm, DEFAULT_PART_CM), nomenclatureIds: [] },
  ];
};

/**
 * Qismni olib tashlaydi — uning joyi BO'SHGA aylanadi.
 *
 * Qo'shnisiga qo'shib yuborish ham mumkin edi, lekin u boshqa mahsulot
 * uchun o'lchab qo'yilgan joyni jimgina kengaytirib yuborardi.
 */
export const removePart = (parts: readonly SplitPart[], index: number): SplitPart[] =>
  parts.filter((_, i) => i !== index).map(clonePart);

/**
 * Proyomni teng qismlarga bo'ladi; qoldiq oxirgi qismga qo'shiladi.
 * Tanlangan mahsulotlar tartib bo'yicha SAQLANADI.
 */
export const splitEqually = (
  parts: readonly SplitPart[],
  bayCm: number,
  count: number,
): SplitPart[] => {
  if (count < 1) return parts.map(clonePart);
  const each = Math.floor(bayCm / count);
  if (each < 1) return parts.map(clonePart);

  return Array.from({ length: count }, (_, index) => ({
    widthCm: index === count - 1 ? bayCm - each * (count - 1) : each,
    nomenclatureIds: [...(parts[index]?.nomenclatureIds ?? [])],
  }));
};

/**
 * Bo'linishni FAQAT BITTA QATORGA qo'yadi.
 *
 * Qo'shni qatorlar TEGILMAYDI va bu butun ishning ma'nosi: ajratgich
 * javonning kerakli taxtasiga qo'yiladi, qolganlari o'z holicha qoladi.
 * Ilgari bo'linish hamma qatorga tushardi va bitta o'rinni bo'lish
 * javonning butun ustunini bo'lib yuborardi.
 *
 * Bo'linayotgan proyom O'RNIDA qismlarga almashadi, o'sha qatordagi
 * qolganlari ham tegilmaydi — shuning uchun chapdagi kataklar o'z kodi
 * bilan qoladi va ularga bosilgan etiketkalar kuchda qolaveradi.
 *
 * @param level qator raqami, 1 dan boshlanadi
 * @param index o'sha qatordagi proyom o'rni, 0 dan boshlanadi
 */
export const applySplit = (
  baysM: readonly (readonly number[])[],
  level: number,
  index: number,
  partsCm: readonly number[],
): number[][] => {
  const row = baysM[level - 1];
  if (row === undefined) throw new Error('Bunday qator yo\'q');
  if (index < 0 || index >= row.length) throw new Error('Bunday proyom yo\'q');

  const nextRow = [...row];
  nextRow.splice(index, 1, ...partsCm.map((part) => Math.round(part) * UNIT_M));

  return baysM.map((current, i) => (i === level - 1 ? nextRow : [...current]));
};

/**
 * BUTUN QATORNI qaytadan bo'ladi.
 *
 * `applySplit` bitta o'rinni bo'ladi, bu esa qatorning HAMMASINI
 * almashtiradi: omborchi javonni mahsulotga tayyorlayotganda "shu taxtani
 * beshta ellik santimetrlik joyga bo'lib qo'y" deydi va har o'rinni
 * alohida bo'lib chiqish ortiqcha ish bo'lardi.
 *
 * Qo'shni qatorlar bu yerda ham TEGILMAYDI.
 *
 * @param level qator raqami, 1 dan boshlanadi
 */
export const applyLevel = (
  baysM: readonly (readonly number[])[],
  level: number,
  partsCm: readonly number[],
): number[][] => {
  if (baysM[level - 1] === undefined) throw new Error('Bunday qator yo\'q');
  if (partsCm.length === 0) throw new Error('Qator bo\'sh qololmaydi');

  return baysM.map((row, index) =>
    index === level - 1 ? partsCm.map((part) => Math.round(part) * UNIT_M) : [...row],
  );
};

/** Metrdagi o'lchamni ekran santimetriga o'giradi. */
export const toCm = (metres: number): number => Math.round(metres / UNIT_M);
